import { useState, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import {
    Card,
    CardHeader,
    CardBody,
} from "@material-tailwind/react";
import { sortResponses } from "utils/utils";
import ThemingTabHeader from "./theming-tab-header";
import ThemingTabFilters from "./theming-tab-filters";
import ResponseThemesDialog from "./dialog/theme-responses";
import ManageThemesDialog from "./dialog/manage-themes-dialog";
import useSWR from "swr";
import { fetcherFull, fetcherSimple } from "utils/api";
import { createSurveyCacheInvalidator } from "utils/invalidate-survey-caches";
import { updateThemeName } from "./utils/update-theme-name";
import { toast } from "react-toastify";
import { useLoading } from "contexts/loading-context";
import { useThemeAssignment } from "./hooks/use-theme-assignment";
import { useResponsesData } from "./hooks/use-responses-data";
import SelectedRowsBar from "./responses/selected-rows-bar";
import ResponsesTable from "./responses/responses-table";
import { COMPREHEND_TIMEOUT_MS, INITIAL_FILTERS } from "./theming-tab.config";

// Hoisted so the onCounterUpdate default has stable identity across renders.
// A inline `() => {}` default would create a new function each render and
// re-fire the counter-propagation useEffect in useResponsesData on every tick.
const NOOP = () => {};

export default function ThemingTab(props) {
    const { watch, setValue, reset, getValues } = useFormContext();
    const {
        buildThemesDataMutate = NOOP,
        onCounterUpdate = NOOP,
    } = props;
    const { startLoading, stopLoading } = useLoading();

    const invalidateSurveyCaches = createSurveyCacheInvalidator(props.surveyId);

    const [selectedRows, setSelectedRows] = useState([]);
    const [togglingAssist, setTogglingAssist] = useState(false);
    const togglingAssistRef = useRef(false);
    const [comprehendProcessing, setComprehendProcessing] = useState(false);
    const comprehendProcessingRef = useRef(false);
    const mountedRef = useRef(true);
    const [filtersOpen, setFiltersOpen] = useState(true);
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState("asc");
    const [textSearch, setTextSearch] = useState("");
    const [responseThemesToViewId, setResponseThemesToViewId] =
        useState(undefined);
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    // Dialogs
    const [showAddToThemeDialog, setShowAddToThemeDialog] = useState(false);
    const [showRemoveFromThemeDialog, setShowRemoveFromThemeDialog] =
        useState(false);
    const [showResponseThemesDialog, setShowResponseThemesDialog] =
        useState(false);
    const [showManageThemesDialog, setShowManageThemesDialog] = useState(false);

    const {
        data: themesData,
        mutate: themesDataMutate,
    } = useSWR(
        [`/v2/survey/${props.surveyId}/theme?rows=99999`, "GET"],
        ([path, method]) => fetcherSimple(path, method),
    );

    const {
        customResponsesFilter,
        customResponsesFilterPager,
        customResponsesFilterSize,
        customResponsesFilterSetSize,
        customResponsesFilterLoading,
        customResponsesFilterMutate,
        customResponsesFilterIsReachingEnd,
        counter,
        dropdownThemeCounts,
        dropdownQuestionCounts,
        allResponses,
    } = useResponsesData({
        surveyId: props.surveyId,
        filters,
        textSearch,
        comprehendProcessing,
        onCounterUpdate,
    });

    // Live read of the form flag: the header switch and the toggle handler
    // below drive it through setValue, so watch keeps every consumer
    // (filters, table columns) in step without a local mirror state.
    const gobbyAssist = Boolean(watch("use_comprehend"));

    // Timeout: stop polling after 60 seconds
    useEffect(() => {
        if (!comprehendProcessing) return;

        const timeout = setTimeout(() => {
            setComprehendProcessing(false);
            comprehendProcessingRef.current = false;
            stopLoading();
            toast.info("Gobby Assist is still processing responses in the background. Data will appear when ready.");
        }, COMPREHEND_TIMEOUT_MS);

        return () => clearTimeout(timeout);
    }, [comprehendProcessing, stopLoading]);

    // Mirror stopLoading in a ref so the unmount cleanup below can call
    // the latest version without re-running on every stopLoading identity
    // change (which would fire mid-lifecycle and leave mountedRef stuck
    // at false, disabling subsequent comprehend setup).
    const stopLoadingRef = useRef(stopLoading);
    useEffect(() => {
        stopLoadingRef.current = stopLoading;
    }, [stopLoading]);

    // Unmount cleanup: ensure loading bar is removed if user navigates away.
    // Empty deps so this only fires on real unmount, not on every
    // stopLoading identity change.
    useEffect(() => {
        return () => {
            mountedRef.current = false;
            if (comprehendProcessingRef.current) {
                stopLoadingRef.current?.();
            }
        };
    }, []);

    const onToggleGobbyAssist = async (newVal) => {
        if (togglingAssistRef.current) return;
        togglingAssistRef.current = true;
        setTogglingAssist(true);

        setValue("use_comprehend", newVal, { shouldDirty: true });

        try {
            const res = await fetcherFull(
                `/v2/survey/${props.surveyId}`,
                "PUT",
                { use_comprehend: newVal },
            );

            if (!res?.name) {
                throw new Error(res?.error || "Unexpected response from server");
            }

            // Reset the form baseline so dirty/unsaved-changes state clears.
            // getValues() reads the store at save time, so concurrent edits
            // survive the re-baseline (latestValuesRef's old job).
            reset({ ...getValues(), use_comprehend: newVal });

            invalidateSurveyCaches();

            if (newVal && mountedRef.current) {
                // Turning ON: start polling for Comprehend data
                setComprehendProcessing(true);
                comprehendProcessingRef.current = true;
                startLoading();
                // Trigger immediate revalidation (fire-and-forget; polling retries on failure)
                customResponsesFilterMutate().catch((err) => {
                    console.warn("Comprehend data revalidation failed:", err);
                });
            } else if (!newVal) {
                // Turning OFF: stop any active processing
                if (comprehendProcessingRef.current) {
                    setComprehendProcessing(false);
                    comprehendProcessingRef.current = false;
                    stopLoading();
                }
            }
        } catch (err) {
            console.error("Gobby Assist toggle failed:", err);
            // Rollback optimistic update
            setValue("use_comprehend", !newVal, { shouldDirty: true });
            toast.error("Failed to update Gobby Assist.");
        } finally {
            togglingAssistRef.current = false;
            setTogglingAssist(false);
        }
    };

    // Completion detection: check if Comprehend has finished processing
    useEffect(() => {
        if (!comprehendProcessing) return;
        if (!customResponsesFilter || customResponsesFilter.length === 0) return;

        const allPagesData = customResponsesFilter.flatMap(
            (page) => page?.data || [],
        );

        if (allPagesData.length === 0) {
            setComprehendProcessing(false);
            comprehendProcessingRef.current = false;
            stopLoading();
            return;
        }

        const allHaveResults = allPagesData.every(
            (r) => r.sentiment !== null && r.keyphrases !== null,
        );
        if (allHaveResults) {
            setComprehendProcessing(false);
            comprehendProcessingRef.current = false;
            stopLoading();
        }
    }, [customResponsesFilter, comprehendProcessing, stopLoading]);

    const emptyFilter = () => {
        setFilters(INITIAL_FILTERS);
        setTextSearch("");
        setSelectedRows([]);
    };

    const addFilter = (type, obj) => {
        if (
            filters?.[type]?.length < 1 ||
            !filters?.[type]?.some((f) => f.id === obj.id)
        ) {
            setFilters({
                ...filters,
                [type]: [...filters[type], obj],
            });
        }
    };

    const removeFilter = (type, id) => {
        setFilters((prev) => ({
            ...prev,
            [type]: prev[type].filter((f) => f.id !== id),
        }));
    };

    const responses = allResponses.filter((r) =>
        selectedRows.includes(r.answer_nanoid),
    );

    const sortedResponses = sortResponses(
        allResponses,
        sortField,
        sortDirection,
    );

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection("asc");
        }
    };

    const handleSelectAll = (checked) => {
        if (checked) setSelectedRows(allResponses.map((r) => r.answer_nanoid));
        else setSelectedRows([]);
    };

    const handleSelectRow = (answer_nanoid, checked) => {
        if (checked) setSelectedRows([...selectedRows, answer_nanoid]);
        else
            setSelectedRows(
                selectedRows.filter((rowId) => rowId !== answer_nanoid),
            );
    };

    const handleAddKeyword = (e) => {
        const keyword = e.target.innerText;
        const newId = filters?.keywords?.length + 1;
        if (keyword) {
            addFilter("keywords", {
                id: newId,
                text: keyword,
            });
        }
    };

    const { addToTheme: handleAddToTheme, removeFromTheme: handleRemoveFromTheme } =
        useThemeAssignment({
            themesDataMutate,
            buildThemesDataMutate,
            customResponsesFilterMutate,
            invalidateSurveyCaches,
            setSelectedRows,
        });
    const handleEditThemeName = async (id, newName) => {
        const { ok } = await updateThemeName({ surveyId: props.surveyId, themeId: id, name: newName });
        if (ok) {
            themesDataMutate().catch((err) => console.warn("Theme cache refresh failed:", err));
            buildThemesDataMutate().catch((err) => console.warn("Build theme cache refresh failed:", err));
            customResponsesFilterMutate().catch((err) => console.warn("Response cache refresh failed:", err));
            invalidateSurveyCaches();
            toast.success("Theme Edited");
        }
    };

    const handleOpenManageThemes = () => {
        setShowManageThemesDialog(true);
    };

    return (
        <>
            <Card className="card w-full mx-auto shadow-sm">
                <CardHeader
                    floated={false}
                    shadow={false}
                    className="card-inner-header m-0"
                >
                    <ThemingTabHeader
                        disabled={togglingAssist}
                        onToggleGobbyAssist={onToggleGobbyAssist}
                        survey={props.survey}
                        onManageThemes={handleOpenManageThemes}
                    />
                </CardHeader>

                <CardBody className="card-inner p-2 pt-0 !bg-white">
                    <ThemingTabFilters
                        gobbyAssist={gobbyAssist}
                        filtersOpen={filtersOpen}
                        setFiltersOpen={setFiltersOpen}
                        allResponses={allResponses}
                        textSearch={textSearch}
                        setTextSearch={setTextSearch}
                        filters={filters}
                        addFilter={addFilter}
                        setFilters={setFilters}
                        removeFilter={removeFilter}
                        emptyFilter={emptyFilter}
                        surveyQuestions={props.surveyQuestions}
                        themesData={themesData}
                        customResponsesFilterPager={customResponsesFilterPager}
                        handleEditThemeName={handleEditThemeName}
                        themesDataMutate={themesDataMutate}
                        customResponsesFilterMutate={
                            customResponsesFilterMutate
                        }
                        surveyId={props.surveyId}
                        sortedResponses={sortedResponses}
                        dropdownThemeCounts={dropdownThemeCounts}
                        dropdownQuestionCounts={dropdownQuestionCounts}
                        counter={counter}
                    />
                    <SelectedRowsBar
                        selectedCount={selectedRows.length}
                        showAddToThemeDialog={showAddToThemeDialog}
                        showRemoveFromThemeDialog={showRemoveFromThemeDialog}
                        onOpenAddToTheme={() => setShowAddToThemeDialog(true)}
                        onOpenRemoveFromTheme={() => setShowRemoveFromThemeDialog(true)}
                        onCloseAddToTheme={() => setShowAddToThemeDialog(false)}
                        onCloseRemoveFromTheme={() => setShowRemoveFromThemeDialog(false)}
                        themes={themesData?.data}
                        responses={responses}
                        onAddToTheme={handleAddToTheme}
                        onRemoveFromTheme={handleRemoveFromTheme}
                        surveyId={props.surveyId}
                        themesDataMutate={themesDataMutate}
                        customResponsesFilterMutate={customResponsesFilterMutate}
                        handleEditThemeName={handleEditThemeName}
                    />
                    <ResponsesTable
                        selectedRows={selectedRows}
                        allResponses={allResponses}
                        filters={filters}
                        sortedResponses={sortedResponses}
                        sortField={sortField}
                        sortDirection={sortDirection}
                        gobbyAssist={gobbyAssist}
                        onSelectAll={handleSelectAll}
                        onSelectRow={handleSelectRow}
                        onSort={handleSort}
                        onAddKeyword={handleAddKeyword}
                        onOpenResponseThemes={(answerNanoid) => {
                            setResponseThemesToViewId(answerNanoid);
                            setShowResponseThemesDialog(true);
                        }}
                        customResponsesFilterPager={customResponsesFilterPager}
                        customResponsesFilterSize={customResponsesFilterSize}
                        customResponsesFilterSetSize={customResponsesFilterSetSize}
                        customResponsesFilterIsReachingEnd={customResponsesFilterIsReachingEnd}
                        customResponsesFilterLoading={customResponsesFilterLoading}
                    />
                </CardBody>
            </Card>
            <ResponseThemesDialog
                open={showResponseThemesDialog}
                onClose={() => {
                    setShowResponseThemesDialog(false);
                    setResponseThemesToViewId(undefined);
                }}
                themes={themesData?.data?.filter((theme) => {
                    return Object.keys(
                        allResponses.find(
                            (response) =>
                                response.answer_nanoid ===
                                responseThemesToViewId,
                        )?.themes || {},
                    )
                        ?.map(Number)
                        ?.includes(theme.id);
                })}
                themesDataMutate={themesDataMutate}
                surveyId={props.surveyId}
                customResponsesFilterMutate={customResponsesFilterMutate}
            />
            <ManageThemesDialog
                open={showManageThemesDialog}
                onClose={() => setShowManageThemesDialog(false)}
                themes={themesData?.data}
                themeCounts={dropdownThemeCounts}
                surveyId={props.surveyId}
                themesDataMutate={themesDataMutate}
                customResponsesFilterMutate={customResponsesFilterMutate}
            />
        </>
    );
}
